// Adapted from D:/AI/template-site-factory/src/components/seo/PageHead.tsx
import { Head } from 'vite-react-ssg';
import { getLang, langMeta, stripEn, withLang } from '@/lib/lang';

interface PageHeadProps {
  title: string;
  description: string;
  path?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  noindex?: boolean;
  jsonLd?: object | object[];
}

const siteUrl = 'https://queensbutler.jp';
const serializeJsonLd = (value: object) => JSON.stringify(value).replace(/</g, '\\u003c');

export function PageHead({
  title,
  description,
  path = '/',
  canonical,
  ogImage,
  ogType = 'website',
  noindex = false,
  jsonLd,
}: PageHeadProps) {
  const url = canonical?.startsWith('http') ? canonical : `${siteUrl}${canonical ?? path}`;
  // 言語は path(自ページ URL)から判定。canonical は自言語、hreflang は ja / en / x-default(=ja) を両言語ページに出す(仕様 §2-6)
  const lang = getLang(path);
  const jaUrl = `${siteUrl}${stripEn(path)}`;
  const enUrl = `${siteUrl}${withLang(path, 'en')}`;
  const imageUrl = ogImage ? (ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`) : undefined;
  const jsonLdItems = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Head>
      <html lang={langMeta[lang].hreflang} className={`lang-${lang}`} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="ja" href={jaUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={jaUrl} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Queen's Butler" />
      <meta property="og:locale" content={langMeta[lang].ogLocale} />
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      {imageUrl && <meta property="og:image:alt" content={title} />}
      <meta name="twitter:card" content={imageUrl ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      {jsonLdItems.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {serializeJsonLd(schema)}
        </script>
      ))}
    </Head>
  );
}
